import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Container, CircularProgress, Typography, Alert } from '@mui/material';
import { lookupByAuthorizationId, lookupByAuthorizationCode, lookupByETicket } from '../../services';
import { ITransactionLookupResult, ITransactionOption } from '../../types';
import { TransactionSelector } from 'features/transaction-details/components/TransactionSelector';

export const TransactionLookup = (): JSX.Element => {
	const history = useHistory();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [transactionOptions, setTransactionOptions] = useState<ITransactionOption[]>([]);
	const [selectorOpen, setSelectorOpen] = useState(false);

	const authorizationId = searchParams.get('authorizationId');
	const authorizationCode = searchParams.get('authorizationCode');
	const eticket = searchParams.get('eticket');

	const navigateToTransaction = useCallback(
		(result: ITransactionLookupResult): void => {
			const params = new URLSearchParams();
			if (result.highlightAuthorizationId) {
				params.set('highlightAuth', result.highlightAuthorizationId.toString());
			}
			if (result.highlightEticket) {
				params.set('highlightEticket', result.highlightEticket);
			}

			const queryString = params.toString();
			history.push(`/transaction-details/${result.transactionId}${queryString ? `?${queryString}` : ''}`);
		},
		[history],
	);

	const performLookup = useCallback(async (): Promise<void> => {
		setLoading(true);
		setError(null);

		try {
			let result: ITransactionLookupResult | ITransactionOption[];

			if (authorizationId) {
				result = await lookupByAuthorizationId(parseInt(authorizationId));
			} else if (authorizationCode) {
				result = await lookupByAuthorizationCode(authorizationCode);
			} else if (eticket) {
				result = await lookupByETicket(eticket);
			} else {
				setError('No se proporcionó ningún parámetro de búsqueda válido');
				setLoading(false);
				return;
			}

			// Si es un array, mostrar selector
			if (Array.isArray(result)) {
				setTransactionOptions(result);
				setSelectorOpen(true);
				setLoading(false);
			} else {
				// Si es un resultado único, redirigir
				navigateToTransaction(result);
			}
		} catch (err) {
			console.error('Error en lookup:', err);
			setError(err instanceof Error ? err.message : 'Error al buscar la transacción');
			setLoading(false);
		}
	}, [authorizationId, authorizationCode, eticket, navigateToTransaction]);

	useEffect(() => {
		performLookup();
	}, [performLookup]);

	const handleSelectTransaction = (transactionId: number): void => {
		const result: ITransactionLookupResult = {
			transactionId,
			highlightEticket: eticket || undefined,
		};
		navigateToTransaction(result);
	};

	const handleCloseSelector = (): void => {
		setSelectorOpen(false);
		history.goBack();
	};

	if (loading) {
		return (
			<Container
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '60vh',
					gap: 2,
				}}
			>
				<CircularProgress size={60} />
				<Typography variant="h6" color="text.secondary">
					Buscando transacción...
				</Typography>
			</Container>
		);
	}

	if (error) {
		return (
			<Container sx={{ mt: 4 }}>
				<Alert severity="error">{error}</Alert>
			</Container>
		);
	}

	return (
		<>
			<TransactionSelector
				open={selectorOpen}
				transactions={transactionOptions}
				onSelect={handleSelectTransaction}
				onClose={handleCloseSelector}
			/>
		</>
	);
};
